import React, { type ComponentProps, type ReactNode } from 'react'

import { TextBodyBold } from '../../data-display/typography/Typography'

import { StyledTab, StyledTabs, TAB_PADDING_X } from './Tabs.styled'
import { Badge, BadgeStatus } from '../../data-display/badge/Badge'
import { IconButton } from '../../inputs/icon-button/IconButton'
import Box from '@mui/material/Box'
import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined'
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined'
import Tooltip from '@mui/material/Tooltip'

export type TabItemProps = {
  ariaControls?: string
  label: string | ReactNode
  icon?: ReactNode
  disabled?: boolean
  id?: string
  value?: number | string
  redPill?: boolean
  /** Colour of the `redPill` dot — defaults to the red "new" status. */
  pillStatus?: BadgeStatus
  tooltip?: string
  'data-testid'?: string
}

export type TabsProps = {
  items: Omit<TabItemProps, 'selected'>[]
  onChange:
    | ((e: React.ChangeEvent<unknown>, newValue: number) => void)
    | ((e: React.ChangeEvent<unknown>, newValue: string) => void)
  /** `false` selects no tab — use it while the panel shows unsaved content that no tab represents. */
  value: number | string | false
  ariaLabel?: string
  variant?: 'fullWidth' | 'scrollable' | 'standard'
  scrollButtons?: 'auto' | true | false
}

const a11yProps = (ariaLabel: string | undefined, index: number) =>
  ariaLabel
    ? {
        id: `${ariaLabel}-tab-${index}`,
        'aria-controls': `${ariaLabel}-tabpanel-${index}`,
      }
    : undefined

type TabsScrollButtonProps = {
  direction: 'left' | 'right'
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const TabsScrollButton = ({
  direction,
  disabled = false,
  onClick,
}: TabsScrollButtonProps) => (
  <IconButton
    size="small"
    onClick={onClick}
    disabled={disabled}
    ariaLabel={direction === 'left' ? 'Scroll tabs left' : 'Scroll tabs right'}
    sx={{
      // The Tabs root is a flex container the height of the strip; keep the
      // button square and centered so its round hover stays a circle.
      alignSelf: 'center',
      flexShrink: 0,
    }}
  >
    {direction === 'left' ? <ChevronLeftOutlined /> : <ChevronRightOutlined />}
  </IconButton>
)

type TabWithTooltipProps = ComponentProps<typeof StyledTab> & {
  tooltip: string
  /** Injected by MUI's Tabs on each child; absent from Tab's public props. */
  fullWidth?: boolean
}

const TabWithTooltip = ({ tooltip, ...tabProps }: TabWithTooltipProps) => (
  <Tooltip title={tooltip}>
    {/* The wrapper is the flex item, so it takes the sizing MUI gives the Tab.
        When `fullWidth`, its basis is the Tab's padding, the outer basis of a
        bare Tab, so both get the same share of the strip. Otherwise it never
        shrinks, or a wrapped tab collapses to its longest word and overlaps
        its neighbours. */}
    <Box
      component="span"
      sx={(theme) => ({
        display: 'flex',
        ...(tabProps.fullWidth
          ? {
              flex: `1 1 ${theme.spacing(TAB_PADDING_X * 2)}`,
              minWidth: 'fit-content',
            }
          : { flexShrink: 0 }),
      })}
    >
      <StyledTab {...tabProps} />
    </Box>
  </Tooltip>
)

export const Tabs = ({
  items,
  onChange,
  value,
  ariaLabel,
  variant = 'standard',
  scrollButtons,
}: TabsProps) => {
  // A scrollable strip defaults to `auto` (buttons appear on overflow, hide once
  // every tab fits); the other variants never scroll. An explicit prop wins.
  const resolvedScrollButtons =
    scrollButtons ?? (variant === 'scrollable' ? 'auto' : false)

  return (
    <StyledTabs
      value={value}
      onChange={onChange}
      aria-label={ariaLabel}
      variant={variant}
      scrollButtons={resolvedScrollButtons}
      // MUI hides the scroll buttons below `sm` by default; keep them so the
      // strip stays navigable at every width.
      allowScrollButtonsMobile
      // Swap MUI's default arrows for the DS IconButton. Only wire the slot when
      // buttons can show (`false` would never mount it anyway).
      slots={
        resolvedScrollButtons !== false
          ? { scrollButtons: TabsScrollButton }
          : undefined
      }
    >
      {items.map((item, index) => {
        const tabProps = {
          label: (
            <TextBodyBold
              sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {item.icon}
              {item.label}
              {item.redPill && (
                <Box
                  // A span: the label wrapper is a <p>, which cannot legally
                  // contain a <div>.
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'start',
                    height: '100%',
                  }}
                >
                  <Badge
                    size="xs"
                    status={item.pillStatus ?? BadgeStatus.NEW}
                  />
                </Box>
              )}
            </TextBodyBold>
          ),
          disabled: item.disabled ?? false,
          value: item.value ?? index,
          'data-testid': item['data-testid'],
          ...a11yProps(ariaLabel, index),
        }

        return item.tooltip ? (
          <TabWithTooltip key={index} tooltip={item.tooltip} {...tabProps} />
        ) : (
          <StyledTab key={index} {...tabProps} />
        )
      })}
    </StyledTabs>
  )
}
